/**
 * @namespace StickyNavigation
 * @memberof Promega.Components
 * @description Methods for controlling sticky navigation components that allow smooth scrolling between different page sections. See examples for details on how to add an anchor link to the sticky navigation.
 * 
 * @example <caption>The sticky navigation, itself, consists of a full-width container that wraps an inner content-width section. On page load,
 * the component iterates over all the links within the sticky nav and locates their targets on the page. If a target can't be found for a link,
 * the link is removed from the sticky nav. Nav links are highlighted to indicate the current section as the user scrolls down the page.</caption>
 * <section class="sticky-navigation sticky-navigation--secondary full-width no-bottom-margin">
 *   <nav class="content-width">
 *      <a href="#overview" class="sticky-navigation__link">Overview</a>
 *      <a href="#specifications" class="sticky-navigation__link">Specifications</a>
 *   </nav>
 * </section>
 */
import smoothScrollIntoView from '../util/smoothScrollIntoView';
import runJS from '../util/runJS';
import trackEvent from '../util/trackEvent';

const NAV_LINK_CLASS = "sticky-navigation__link",
    NAV_LINK_ACTIVE_CLASS = "sticky-navigation__link--active";

let stickyNavElement;
const intersecting = [];
const headerElement = document.querySelector('.site-header');

// Private functions/listeners

function remToPx(rem) {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}


export function handleStickyClick(event) {
    event.preventDefault();
    const destination = document.querySelector(event.target.getAttribute("href"));
    const stickyNavOffset = stickyNavElement ? getOffset(stickyNavElement) : 0;
    const headerOffset = (headerElement ?
        headerElement.querySelector('.site-header__content').offsetHeight +
        headerElement.querySelector('.site-header__nav').offsetHeight
        : 0);
    let totalOffset = -1 * (stickyNavOffset + headerOffset + remToPx(1));
    smoothScrollIntoView(destination, null, {
        updateHash: true,
        offset: totalOffset
    });
}

function checkStickyNavItemClick (event) {
    handleStickyClick(event);
    trackEvent("Product Detail Page", "Click", `Clicked sticky nav section heading - ${event.target.innerText}`);
}

// API
export function getOffset(el) {
    const secondary = isSecondary(el);
    return el.offsetHeight * (secondary ? 2 : 1);
}

export function isSecondary(el) {
    return el && el.classList.contains('sticky-navigation--secondary');
}

export function getCurrentSection(entries, observer) {
    for (const entry of entries) {
        if (entry.isIntersecting && !intersecting.includes(entry.target))
            intersecting.push(entry.target);
        else if (intersecting.includes(entry.target) && !entry.isIntersecting)
            intersecting.splice(intersecting.indexOf(entry.target), 1);
    }

    let currentSection, currentTop = Infinity;
    for (const element of intersecting) {
        const top = element.getBoundingClientRect().top;
        if (top < currentTop) {
            currentTop = top;
            currentSection = element;
        }
    }

    return currentSection;
}

function observerCallback(entries, observer) {
    let currentSection = getCurrentSection(entries, observer);

    if (currentSection) {
        const active = document.querySelector(`.${NAV_LINK_ACTIVE_CLASS}`);
        active && active.classList.remove(NAV_LINK_ACTIVE_CLASS);

        const linkToActivate = document.querySelector(`.${NAV_LINK_CLASS}[href="#${currentSection.id}"]`);
        if (linkToActivate.hasAttribute("data-gtm")) {
            runJS(linkToActivate.getAttribute("data-gtm"));
        }

        linkToActivate.classList.add(NAV_LINK_ACTIVE_CLASS);
    }
}

// Init code

if (typeof document !== "undefined") {
    stickyNavElement = document.querySelector(".sticky-navigation");
    if (stickyNavElement) {
        if (window.Stickyfill) Stickyfill.add(stickyNavElement);

        // Use the height of the sticky header for the top rootMargin
        const topMargin = stickyNavElement.clientHeight * (isSecondary(stickyNavElement) ? 2 : 1);
        const observer = new IntersectionObserver(observerCallback, { rootMargin: `-${topMargin}px 0% -50% 0%` });
        const navLinks = document.getElementsByClassName(NAV_LINK_CLASS);
        const navLinksCopy = [...navLinks];

        for (let i = 0; i < navLinksCopy.length; i++) {
            const href = navLinksCopy[i].getAttribute("href");
            let destination;
            try {
                destination = document.querySelector(href);
            } catch (e) {
                continue;
            }

            if (destination) {
                observer.observe(destination);
                navLinksCopy[i].addEventListener("click", checkStickyNavItemClick);
            }
            else
                navLinksCopy[i].parentElement.removeChild(navLinksCopy[i]);
        }

        if (navLinks.length > 0)
            navLinks[0].classList.add(NAV_LINK_ACTIVE_CLASS);
    }
}
